import { forwardRef } from 'react';
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select as MuiSelect,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { colorTokens, radiusTokens, typographyTokens } from '../../app/designTokens';

const { semantic } = colorTokens;

const fieldSx = (isCompact, error) => ({
  minHeight: isCompact ? 40 : 44,
  borderRadius: `${radiusTokens.search}px`,
  bgcolor: semantic.bg.input,
  color: semantic.text.primary,
  fontFamily: typographyTokens.fontFamily,
  fontSize: 13,
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: error ? semantic.error : semantic.border.default,
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: error ? semantic.error : semantic.border.default,
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: error ? semantic.error : semantic.text.secondary,
    borderWidth: 1,
  },
  '&.Mui-disabled': {
    bgcolor: semantic.action.secondary.bg,
    color: semantic.text.disabled,
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: semantic.border.subtle,
    },
  },
  '& .MuiSelect-icon': {
    color: semantic.text.muted,
  },
});

/**
 * Shared Form / Select (Figma Base).
 * @param {'Normal'|'Compact'|'normal'|'compact'} [density]
 */
export const Select = forwardRef(function Select(
  {
    density = 'Normal',
    error = false,
    disabled = false,
    label,
    helperText,
    hideLabel = false,
    children,
    value,
    onChange,
    fullWidth = false,
    minWidth,
    sx,
    MenuProps,
    inputProps,
    'data-testid': dataTestId,
    ...rest
  },
  ref,
) {
  const isCompact = density === 'Compact' || density === 'compact';
  const showLabel = Boolean(label) && !hideLabel;
  const resolvedTestId = dataTestId || inputProps?.['data-testid'];
  const controlSx = {
    ...(minWidth != null ? { minWidth } : null),
    ...(fullWidth ? { width: '100%' } : null),
    ...sx,
  };

  return (
    <FormControl
      ref={ref}
      size={isCompact ? 'small' : 'medium'}
      error={error}
      disabled={disabled}
      fullWidth={fullWidth}
      sx={controlSx}
      data-testid={resolvedTestId}
    >
      {showLabel && (
        <InputLabel
          shrink
          sx={{
            fontFamily: typographyTokens.fontFamily,
            fontWeight: typographyTokens.fontWeightSemibold,
            fontSize: 13,
            color: semantic.text.secondary,
            '&.Mui-focused': { color: semantic.text.secondary },
            '&.Mui-error': { color: semantic.error },
          }}
        >
          {label}
        </InputLabel>
      )}
      <MuiSelect
        label={showLabel ? label : undefined}
        notched={showLabel}
        displayEmpty
        value={value}
        onChange={onChange}
        IconComponent={KeyboardArrowDownIcon}
        inputProps={{
          'aria-label': !showLabel && label ? label : undefined,
          ...(inputProps || {}),
          // Keep test id on FormControl so within(testid).getByRole('combobox') works.
          'data-testid': undefined,
        }}
        MenuProps={{
          ...MenuProps,
          PaperProps: {
            ...MenuProps?.PaperProps,
            sx: {
              bgcolor: semantic.bg.surface,
              backgroundImage: 'none',
              border: `1px solid ${semantic.border.subtle}`,
              borderRadius: `${radiusTokens.md}px`,
              mt: 0.5,
              '& .MuiMenuItem-root': {
                fontFamily: typographyTokens.fontFamily,
                fontSize: 13,
                color: semantic.text.secondary,
                minHeight: 36,
                '&:hover': {
                  bgcolor: semantic.action.secondary.bg,
                  color: semantic.text.primary,
                },
                '&.Mui-selected': {
                  bgcolor: semantic.bg.surfaceSelected,
                  color: semantic.text.primary,
                  '&:hover': {
                    bgcolor: semantic.bg.surfaceSelected,
                  },
                },
              },
              ...MenuProps?.PaperProps?.sx,
            },
          },
        }}
        sx={fieldSx(isCompact, error)}
        {...rest}
      >
        {children}
      </MuiSelect>
      {helperText ? (
        <FormHelperLike error={error}>{helperText}</FormHelperLike>
      ) : null}
    </FormControl>
  );
});

function FormHelperLike({ children, error }) {
  return (
    <span
      style={{
        display: 'block',
        marginTop: 6,
        fontFamily: typographyTokens.fontFamily,
        fontSize: 12,
        letterSpacing: '0.24px',
        color: error ? semantic.error : semantic.text.muted,
      }}
    >
      {children}
    </span>
  );
}

export const SelectOption = MenuItem;

export default Select;
